import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import DocumentMeta from "react-document-meta";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotoSansJp } from "./TextStyle";
import { useBranch, Navigate } from "./useBranch";
import { blogPosts, commonBlogKeywords, sortPostsByDateDesc } from "../Blog";

// 店舗別ブログ一覧(/:region/blog)。全店舗共通の /blog とは別に、
// その店舗に紐づく記事(storeId === region)だけを表示する。
// 店舗をまたいだ同一内容の複製を避けるため、全社共通記事(storeId: "shared")は
// ここには表示せず、全店舗共通の /blog 側に一本化する。
const RegionBlog = () => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const state = useBranch();
  if (state.status === "loading") return null;
  if (state.status === "notfound") return <Navigate to="/" replace />;
  const branch = state.branch;

  const posts = sortPostsByDateDesc(
    blogPosts.filter((post) => post.storeId === branch.id),
  );

  const navigate = (path: string) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.location.href = path;
  };

  const canonical = `https://tht-group.co.jp/${branch.id}/blog`;
  const meta = {
    title: `${branch.roomName}のブログ - 土日だけ体操教室`,
    description: `${branch.roomName}のブログ。教室での出来事や練習のコツ、指導方針などをお届けします。`,
    canonical,
    auto: { ograph: true },
    meta: {
      charset: "utf-8",
      name: {
        keywords: [...commonBlogKeywords, branch.roomName].join(","),
        "twitter:card": "summary_large_image",
      },
      property: {
        "og:type": "website",
        "og:site_name": "土日だけ体操教室",
        ...(posts[0]
          ? { "og:image": `https://tht-group.co.jp${posts[0].image}` }
          : {}),
      },
    },
  };

  return (
    <DocumentMeta {...meta}>
      <Box sx={{ width: "100vw" }}>
        <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ width: "100vw" }}>
            <Grid
              container
              direction="column"
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Box sx={{ width: "100%", mt: isMobile ? "40px" : "100px" }}>
                <Grid
                  container
                  direction="column"
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    p: isMobile ? "0 7%" : "0",
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      color: "darkorange",
                      fontSize: isMobile ? "20px" : "35px",
                      fontWeight: "bold",
                      pt: isMobile ? "0" : "50px",
                      mb: isMobile ? "10px" : "20px",
                      ...NotoSansJp,
                    }}
                  >
                    {branch.roomName}｜ブログ
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#333333",
                      mb: "40px",
                    }}
                  >
                    <HomeIcon fontSize="small" />
                    <Typography sx={{ fontSize: isMobile ? "14px" : "16px", mx: 1 }}>
                      {">"}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#333333",
                        fontWeight: 500,
                        fontSize: isMobile ? "14px" : "16px",
                        ...NotoSansJp,
                      }}
                    >
                      ブログ
                    </Typography>
                  </Box>
                </Grid>
              </Box>

              <Box sx={{ width: isMobile ? "90vw" : "70vw", m: "0 0 10vh 0" }}>
                {posts.length === 0 ? (
                  <Typography sx={{ color: "#555", textAlign: "center", py: 6 }}>
                    現在準備中です。しばらくお待ちください。
                  </Typography>
                ) : (
                  <Grid container direction="column" spacing={4}>
                    {posts.map((post) => (
                      <Grid
                        item
                        key={post.id}
                        sx={{
                          display: "flex",
                          flexDirection: isMobile ? "column" : "row",
                          gap: isMobile ? 2 : 4,
                          pb: 4,
                          mb: 4,
                          borderBottom: "1px solid #eee",
                          cursor: "pointer",
                        }}
                        onClick={() => navigate(`/${branch.id}/blog/${post.id}`)}
                      >
                        <Box sx={{ width: isMobile ? "100%" : "260px", flexShrink: 0 }}>
                          <Box
                            component="img"
                            src={post.image}
                            alt={post.imageAlt ?? post.title}
                            sx={{
                              width: "100%",
                              height: isMobile ? "180px" : "160px",
                              objectFit: "cover",
                              borderRadius: "12px",
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography sx={{ color: "#999", fontSize: "13px", mb: "6px" }}>
                            {post.date}
                          </Typography>
                          <Typography
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{ color: "#333", fontWeight: 700, mb: "10px" }}
                          >
                            {post.title}
                          </Typography>
                          <Typography sx={{ color: "#555", fontSize: "14px" }}>
                            {post.excerpt}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#EBB609",
                              fontWeight: 700,
                              fontSize: "13px",
                              mt: "12px",
                            }}
                          >
                            続きを読む ＞
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </DocumentMeta>
  );
};

export default RegionBlog;
